import { cva } from 'class-variance-authority';
import { focusRing } from 'components/styles/focusRing';

/**
 * Button class recipe.
 *
 * Deliberately contains ZERO `dark:` variants. Colour comes entirely from the `--ds-btn-*` slot
 * variables, which the `.ds-btn-<appearance>-<fill>` classes in `components/styles/design-system.css`
 * set per theme. One class list therefore serves both themes.
 *
 * Sizing is height-based rather than padding-based on purpose: Figma gives each size a fixed height
 * (16/24/32/40/44), and `fill="muted"` adds a border. With padding-based sizing that border would
 * make muted buttons taller than their solid siblings; with a fixed height plus the app's
 * global `box-sizing: border-box` they line up exactly.
 *
 * THE SCALE. Figma's `Size` axis uses these same five names, at the same heights:
 *
 *   `3xs`      16px, no padding beyond the 2px inset, 12px icon, icon-only
 *   `2xs`      24px, px-2, 16px icon, Label/md
 *   `xs`       32px, px-3, 16px icon, Label/md
 *   `regular`  40px, px-4, 20px icon, Label/lg
 *   `large`    44px, px-4, 20px icon, Label/lg
 *
 * Until 2026-09-28 Figma called them Tiny / ExtraSmall / Small / Medium / Large. The code kept its
 * names, because renaming them would have silently moved every existing `size="xs"` call site from
 * 32px to 24px, and the Figma axis was renamed to match instead. Older comments elsewhere in
 * `src/components` that quote `Size=Small` or `Size=ExtraSmall` use the old Figma words: Small is
 * `xs`, ExtraSmall is `2xs`.
 *
 * `small` (36px) is GONE. It was the one height in the old code scale that the Figma set never drew,
 * and the re-ramp did not bring it back. The one direct product call site moved to `regular`; the
 * three legacy mappers in `common/components/Buttons` reach for `2xs`, `xs` and `regular`. The
 * unsupported step was removed rather than aliased.
 *
 * `2xs` is not icon-only. Figma draws `Size=2xs, iconOnly=No` — a 24px button with a
 * Label/md label and 8px of horizontal padding — so `ButtonSize` admits it and `Button` can build
 * one. It remains the size the Alert's dismiss control uses (HNS-339).
 *
 * `3xs` IS icon-only, and it is the only cell that is. Figma draws `Size=3xs` at `iconOnly=Yes`
 * alone — 66 variants per size and iconOnly value, five sizes icon-only against four labelled — so
 * `IconButtonSize` is what admits it and a 16px button with a label is unconstructable. The same
 * arrangement `2xs` used to have, moved down a step.
 *
 * ⚠️ `3xs` IS BELOW EVERY TOUCH-TARGET FLOOR. A 16px control misses WCAG 2.5.8's 24x24 minimum
 * outright, and `natter-product-rules.md` asks for 44px on every control. Reproduced because the
 * design specifies it, but it must not be the only way to reach an action on a touch surface —
 * pair it with a larger hit area or a second affordance. Raised with design.
 */
export const buttonRecipe = cva(
  [
    'group/btn',
    'relative inline-flex shrink-0 items-center justify-center',
    /**
     * The radius is a SLOT, with the pill as its fallback, so nothing outside a split changes.
     *
     * `SplitButton` sets `--ds-btn-radius` on each of its two halves to give them a pill outer edge
     * and a 4px inner one. A variable rather than a class for one reason: the call bar's toggle half
     * arrives wrapped in tooltip spans, and a custom property inherits through a wrapper where a
     * child selector cannot reach. See `SplitButton.recipe`.
     */
    'rounded-[var(--ds-btn-radius,var(--radius-full))] border-solid',
    'font-sans leading-5 font-medium whitespace-nowrap',
    'cursor-pointer select-none',
    // Phones get an invisible 44px touch target around every size, so the drawn 24/32/40px buttons
    // keep their look while meeting the house 44px floor. Centred, and never narrower than the button.
    "max-sm:before:absolute max-sm:before:top-1/2 max-sm:before:left-1/2 max-sm:before:h-11 max-sm:before:w-[max(100%,--spacing(11))] max-sm:before:-translate-1/2 max-sm:before:content-['']",

    /**
     * ── Motion ────────────────────────────────────────────────────────────────────────────────
     *
     * The property list is written out rather than left as Tailwind's colour shorthand, for one
     * reason: `scale` is in it. That shorthand expands to a fixed colour list, so adding a
     * transform to it means naming the list — and a SECOND `transition-*` utility would not append,
     * it would replace `transition-property` outright and silently take the colours with it.
     *
     * `box-shadow` IS DELIBERATELY NOT IN THE LIST, even though #1180's shared focus ring is a
     * shadow pair and naming the properties made adding it a one-word change. `Radio.recipe` sets
     * out why at length and the reasoning is the same here: a focus indicator that fades in over
     * 150ms is decorative motion on the one affordance that has to be instantaneous, and it makes a
     * posed focus state read the interpolation's FIRST frame — an expanded, fully transparent ring
     * that looks exactly like a ring that never rendered. It is also a paint-tier property, so it
     * would redraw the whole button every frame of every hover.
     *
     * (`Checkbox` and `TextInput` do animate theirs, which is an inconsistency inside the set rather
     * than a decision. It is not widened here; it is raised as its own change.)
     *
     * TWO ROLES ON ONE DECLARATION, which is the whole reason the press reads `--motion-feedback`
     * here rather than on a declaration of its own. `--motion-control` (150ms) is the resting
     * timing; while the button is held, `--motion-feedback` (100ms) replaces it. So the press
     * arrives 50ms sooner than the release settles — which is the asymmetry a press wants, and is
     * what `docs/motion.md` means by "press, active, toggle flip". One `transition-property`, two
     * durations, no second declaration to fall out of step.
     *
     * THE PRESS SCALE is the one piece of genuinely new motion on this component, and it is 3%.
     * Press feedback is felt rather than seen; a tenth of the box reads as a collapse, and anything
     * above ~5% on a 44px control visibly moves the label. It does not contradict the house rule
     * that motion means the system is working: this is the control acknowledging the user's own
     * finger, not a claim about system state — the same category as the hover tint above it.
     *
     * `motion-safe:` rather than a `motion-reduce:` suppression, and that is not a style choice. A
     * lifecycle- or state-qualified rule is a class plus a pseudo-class or attribute — specificity
     * (0,3,0) here — and `motion-reduce:transition-none` is a bare class inside a media query,
     * (0,1,0), because a media query contributes NO specificity. Suppression therefore loses and
     * the scale would play for someone who asked for no motion. Measured in all three engines; see
     * `docs/motion-lifecycle-findings.md`.
     *
     * The `:not([data-disabled])` guard is carried for the same reason every state rule below
     * carries it: a disabled button must not answer a press.
     *
     * ── A finding this work sits next to rather than fixes ─────────────────────────────────────
     *
     * Tailwind v4 wraps its own `hover:` variant in `@media (hover: hover)`, so a tap on a touch
     * device does not leave a hover state stuck on. The hover rules BELOW do not get that, because
     * they are written as arbitrary selectors (`[&:not(...):hover]:`) rather than with the `hover:`
     * variant — an arbitrary variant compiles to the bare selector and no media query. Confirmed in
     * the emitted stylesheet, and the same shape is used by `Tag`, `Menu`, `Select`, `TextInput`
     * and `DatePicker`.
     *
     * So on touch a tapped button keeps its hover tint until something else is tapped. That
     * predates this change; the press feedback added here makes it easier to notice rather than
     * worse. Fixing it means rewriting every hover rule in the layer, which is its own diff — it is
     * recorded in `docs/motion-lifecycle-findings.md` and NOT silently widened here.
     */
    // State the resting scale so the press and release interpolate reliably in every browser.
    'scale-100',
    'transition-[color,background-color,border-color,scale]',
    'duration-[var(--motion-control)] motion-reduce:transition-none',
    '[&:not([data-disabled]):active]:duration-[var(--motion-feedback)]',
    'ease-[var(--motion-control-ease)] [&:not([data-disabled]):active]:ease-[var(--motion-feedback-ease)]',
    'motion-safe:[&:not([data-disabled]):active]:scale-[0.97]',

    // Slot reads. The `.ds-btn-*` class supplies the values.
    'border-[length:var(--ds-btn-border-width)] border-[var(--ds-btn-border)]',
    'bg-[var(--ds-btn-bg)] text-[var(--ds-btn-fg)]',

    // Interactive states, and the three guards that keep all four of them mutually exclusive.
    //
    // `:not([data-disabled])` separates them from the disabled rules below, so a disabled button
    // never has to out-specify a hover or a pressed one. `:not(:active)` separates hover from
    // pressed, which matters because a MOUSE press matches `:hover` and `:active` at once: without
    // it both rules apply, they compile to the same specificity, and the winner is decided by
    // whatever order Tailwind happens to emit its utilities in. With it, exactly one of these four
    // can match any one interaction, so the emitted order stops being able to change what renders.
    //
    // The pressed pair is therefore what BOTH press paths draw — a mouse press and a space or enter
    // press now look alike, where before a mouse press showed the hover pair and only a keyboard
    // press reached the pressed fill. That fill is also why the pressed label exists: the library
    // pairs its active muted fills with a matching `-active` foreground because the resting one
    // over those fills measures 3.9-4.4:1 and misses AA.
    '[&:not([data-disabled]):not(:active):hover]:bg-[var(--ds-btn-bg-hover)]',
    '[&:not([data-disabled]):not(:active):hover]:text-[var(--ds-btn-fg-hover)]',
    '[&:not([data-disabled]):not(:active):hover]:border-[var(--ds-btn-border-hover)]',
    '[&:not([data-disabled]):active]:bg-[var(--ds-btn-bg-pressed)]',
    '[&:not([data-disabled]):active]:text-[var(--ds-btn-fg-pressed)]',
    '[&:not([data-disabled]):active]:border-[var(--ds-btn-border-pressed)]',

    // Base UI sets `data-disabled` for both a native `disabled` and the `focusableWhenDisabled`
    // (aria-disabled) case, so keying off it covers both.
    '[&[data-disabled]]:cursor-not-allowed',
    '[&[data-disabled]]:bg-[var(--ds-btn-bg-disabled)]',
    '[&[data-disabled]]:text-[var(--ds-btn-fg-disabled)]',
    '[&[data-disabled]]:border-[var(--ds-btn-border-disabled)]',

    // Focus. The Figma matrix NOW draws this — `State=Focus` on all nine pairs, carrying the
    // `Focused` effect style and nothing else: the fill, the border and the label are all identical
    // to the resting cell, so focus is purely additive here.
    //
    // `Focused` is two drop shadows, both at zero offset and zero blur: `effect/focus-inner` spread
    // 2 drawn OVER `effect/focus-outer` spread 4. The inner one is the page background, so it reads
    // as a 2px gap rather than as a ring; the visible ring is the outer band it uncovers between 2
    // and 4px. Reproduced as a two-part box-shadow, which is the only property that can draw an
    // offset ring that follows `rounded-full` — an `outline` with an offset does too, but cannot
    // paint the gap in a colour, and the gap is what holds the ring off a same-coloured surface.
    //
    // Every number and colour is a token, including the spreads, so the ring cannot drift from the
    // one the rest of the system draws. `outline-none` suppresses the browser's own ring, which
    // would otherwise sit inside this one.
    focusRing(),
  ],
  {
    variants: {
      size: {
        /**
         * Every icon size below is `--spacing(n)`, never a pixel literal.
         *
         * Figma binds each glyph to its `📏 Spacing` collection (`x3` / `x4` / `x5`), and Tailwind's
         * own scale IS that collection: both key on the pixel value divided by four. That is exactly
         * why `design-system.css` declares no spacing token of its own — see the collection note
         * there — so `--spacing(3)` and Figma's `x3` are one token in two syntaxes, not a conversion.
         *
         * The heights and paddings in the compound variants below were already on the scale through
         * `h-10`, `px-4` and `size-6`. These five declarations were the last values in the recipe
         * still spelling a pixel count, so the whole component now follows the scale if it ever moves.
         *
         * Figma `Size=3xs`. An `x3` glyph in an `x4` box, `x0,5` around it.
         *
         * No type size, unlike every other cell: this one is icon-only, so there is no label to set
         * one for, and declaring one would imply a text form `IconButtonSize` does not admit.
         *
         * See the touch-target warning in the scale note above before reaching for it.
         */
        '3xs': '[--ds-btn-icon-size:--spacing(3)]',
        /**
         * Figma `Size=2xs`. An `x4` glyph in an `x6` box, `x1` of padding around it — the icon
         * size it shares with `xs`, not the `x5` this cell used to draw. Labelled and icon-only forms
         * both exist, so unlike before it carries a type size.
         */
        '2xs': 'text-label-md [--ds-btn-icon-size:--spacing(4)]',
        /** Figma `Size=xs`. */
        xs: 'text-label-md [--ds-btn-icon-size:--spacing(4)]',
        /** Figma `Size=regular`. */
        regular: 'text-label-lg [--ds-btn-icon-size:--spacing(5)]',
        /** Figma `Size=large`. */
        large: 'text-label-lg [--ds-btn-icon-size:--spacing(5)]',
      },
      /** Figma's `Style=Icon` — a square button with a single centred icon and no label. */
      iconOnly: {
        true: 'gap-0 p-0',
        false: 'gap-1',
      },
      /**
       * Busy. Figma NOW draws this — `isLoading=Yes` on all nine pairs, and it says two things: the
       * cell keeps its RESTING fill, border and label colour, and the label itself is replaced by a
       * centred `CircleNotch` glyph at the size's icon size.
       *
       * A loading button is inert, so it carries `data-disabled` and would otherwise pick up the
       * washed-out disabled tokens. Remapping each disabled slot back onto its normal counterpart is
       * what reproduces the first half: the button looks like ITSELF while busy — "working", not
       * "switched off" — with no new colour introduced.
       *
       * The opacity slot is the exception: it goes to 0, not 1, because it is the ONLY thing that
       * decides whether the label shows. It feeds the one opacity rule that reaches the content
       * wrapper (`buttonContentRecipe` below), and while busy the label is meant to be invisible
       * behind the spinner.
       *
       * ONE DELIBERATE DEVIATION. Figma's loading variants have no label node at all, so read
       * literally a busy button would shrink to its spinner. It is not drawn that way either — those
       * variants are authored at a FIXED width wider than the resting cell (183px against 135px at
       * regular), so the file does not actually specify a collapse. The label is therefore kept
       * mounted at zero opacity, which reserves its width and keeps the accessible name; see
       * `buttonContentRecipe`.
       */
      loading: {
        true: [
          '[--ds-btn-bg-disabled:var(--ds-btn-bg)]',
          '[--ds-btn-fg-disabled:var(--ds-btn-fg)]',
          '[--ds-btn-border-disabled:var(--ds-btn-border)]',
          '[--ds-btn-disabled-opacity:0]',
          '[&[data-disabled]]:cursor-progress',
        ],
        false: '',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
      isSelected: {
        true: 'ds-btn-selected',
        false: '',
      },
      appearance: {
        primary: '',
        secondary: '',
        tertiary: '',
        success: '',
        destructive: '',
      },
      fill: {
        default: '',
        muted: '',
      },
    },
    compoundVariants: [
      // Height + horizontal padding, read off the Figma cells: x2 / x3 / x4 / x4 on a 24 / 32 / 40
      // / 44 box. Icon-only is square, so it takes no horizontal padding of its own.
      // `3xs` has no `iconOnly: false` counterpart on purpose — see the scale note above.
      //
      // The min widths (60 / 72 / 88 / 88) stop a short label like "Save" or "Add" rendering as a
      // stub, and are set just above the width of "Cancel" at each size so the usual
      // Cancel + primary pair comes out even. `large` shares `regular`'s label style, so it shares its
      // minimum too. Longer labels still grow past it.
      //
      // Arbitrary values because none of the three is a step in Figma's `📏 Spacing` collection,
      // which stops at x16 (64px), so `noOffScaleSpacing.test.ts` refuses the named-step form. The
      // library has no sizing tokens yet; these move onto them when it does.
      { size: '2xs', iconOnly: false, class: 'h-6 min-w-[60px] px-2' },
      { size: 'xs', iconOnly: false, class: 'h-8 min-w-[72px] px-3' },
      { size: 'regular', iconOnly: false, class: 'h-10 min-w-[88px] px-4' },
      { size: 'large', iconOnly: false, class: 'h-11 min-w-[88px] px-4' },
      { size: '3xs', iconOnly: true, class: 'size-4' },
      { size: '2xs', iconOnly: true, class: 'size-6' },
      { size: 'xs', iconOnly: true, class: 'size-8' },
      { size: 'regular', iconOnly: true, class: 'size-10' },
      { size: 'large', iconOnly: true, class: 'size-11' },

      // Figma draws eight Type × Fill pairs. Primary, Success and Destructive have Default and Muted;
      // Secondary and Tertiary have Default only. Keep this exhaustive over ButtonAppearanceProps:
      // a missing compound variant renders without colour slots rather than raising an error.
      { appearance: 'primary', fill: 'default', class: 'ds-btn-primary-default' },
      { appearance: 'primary', fill: 'muted', class: 'ds-btn-primary-muted' },
      { appearance: 'secondary', fill: 'default', class: 'ds-btn-secondary-default' },
      { appearance: 'tertiary', fill: 'default', class: 'ds-btn-tertiary-default' },
      { appearance: 'success', fill: 'default', class: 'ds-btn-success-default' },
      { appearance: 'success', fill: 'muted', class: 'ds-btn-success-muted' },
      { appearance: 'destructive', fill: 'default', class: 'ds-btn-destructive-default' },
      { appearance: 'destructive', fill: 'muted', class: 'ds-btn-destructive-muted' },
    ],
    defaultVariants: {
      appearance: 'primary',
      fill: 'default',
      size: 'regular',
      iconOnly: false,
      fullWidth: false,
      loading: false,
      isSelected: false,
    },
  }
);

/**
 * Wrapper around the label and icons.
 *
 * This element lets a loading button hide its label while preserving its width and accessible name.
 * Disabled cells use Figma's own foreground tokens at full opacity.
 *
 * The opacity transitions on the control's clock, and it has to: the background beside it already
 * does. Before, a submit button that became enabled tinted its fill over 150ms while its label
 * jumped to full strength at the first frame, and a button that started loading dropped its label
 * in one frame while the spinner appeared in the next. Now the label and the fill arrive together,
 * and the label hands over to the spinner as a cross-fade (see `buttonSpinnerRecipe`).
 */
export const buttonContentRecipe = cva(
  [
    'inline-flex items-center justify-center gap-[inherit]',
    // Zero everywhere except inside a `SplitButton`, which nudges each half's content toward the gap.
    'translate-x-[var(--ds-btn-content-shift,0px)]',
    'transition-opacity duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
    'group-data-[disabled]/btn:opacity-[var(--ds-btn-disabled-opacity)]',
  ],
  {
    variants: {
      /**
       * Hidden with `opacity-0`, NOT unmounted, and deliberately so:
       *
       *   - the label keeps reserving its width, so the button does not shrink the moment it starts
       *     working. A button that collapses mid-submit moves everything beside it.
       *   - the accessible name survives. Removing the text would leave a screen-reader user on an
       *     unnamed button precisely when something is happening. `visibility`/`display` would take
       *     the name with them, which is why this stays an opacity.
       *
       * `opacity-0` alone does NOT hide it. The base list above sets opacity through a
       * `group-data-[disabled]` variant, and a loading button always carries `data-disabled`
       * (`disabled={disabled || loading}`), so that rule always applies — at (0,2,0) it outranks
       * this (0,1,0) utility no matter how the utilities sort. The actual hide therefore comes from
       * `buttonRecipe`'s loading variant driving `--ds-btn-disabled-opacity` to 0; this line is what
       * makes the intent local and readable, and covers a `ButtonContent` rendered busy without
       * `data-disabled`. Both resolve to 0, so they cannot disagree.
       */
      loading: { true: 'opacity-0', false: '' },
    },
    defaultVariants: { loading: false },
  }
);

/**
 * The busy indicator, centred over the hidden label.
 *
 * Sized off `--ds-btn-icon-size` so it tracks the size scale, and coloured by `currentColor` — which
 * on a button resolves to `--ds-btn-fg` — so it is automatically right in every appearance, both
 * fills and both themes. The glyph it wraps is Figma's `CircleNotch`, the shared `SpinnerGlyph`, which carries the rotation
 * (and its reduced-motion pulse) itself; see `ButtonContent`.
 *
 * It fades in on the label's clock, so the two cross over rather than swap: `starting:` is CSS
 * `@starting-style`, the value a newly mounted element transitions FROM. The spinner is unmounted
 * rather than faded out when the work ends, deliberately. Keeping it mounted to fade would keep a
 * spinning glyph in the DOM on a button that is no longer `aria-busy`, which `--motion-spin`'s usage
 * restriction forbids, and the label's own 150ms fade-in already covers the handover.
 */
export const buttonSpinnerRecipe = cva([
  'pointer-events-none absolute inset-0 m-auto',
  'size-[var(--ds-btn-icon-size)]',
  'translate-x-[var(--ds-btn-content-shift,0px)]',
  'transition-opacity duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
  'starting:opacity-0',
]);

/**
 * Sizes any icon passed via `startIcon` / `endIcon` / `children` of an icon-only button.
 *
 * Three declarations, because a DS icon can arrive in two shapes and only one of them obeys the box.
 *
 * `size-*` sizes the SLOT, and that part always worked. `[&_svg]:size-full` was supposed to make the
 * glyph fill it, and for a plain inline `<svg>` it does. For the MUI-backed icons in `src/icons` it
 * does NOT: `SvgIcon` sizes itself `width: 1em; height: 1em` from Emotion, Emotion injects
 * UNLAYERED, and an unlayered declaration beats every `@layer` — so a layered Tailwind utility
 * cannot win against it no matter how specific it looks.
 *
 * `text-[length:…]` is what fixes it, by making `1em` resolve to the icon size rather than fighting
 * over who sets the width. Both shapes then land on exactly `--ds-btn-icon-size`, which the size
 * variants set from the spacing scale.
 *
 * Why this is not cosmetic. Without the font size, `1em` resolved to the nearest INHERITED font
 * size — which on a button is its LABEL. Every glyph was therefore drawn at the label's size rather
 * than Figma's icon size: 12px where the set draws 16 (`2xs`, `xs`) and 14px where it draws 20
 * (`regular`, `large`). The slot variable read correctly the whole time, which is why a check that
 * read the token rather than the rendered box called it correct. `3xs` is what exposed it — it is
 * the one cell that sets no type size, so its glyph inherited Storybook's 13.33px and overflowed a
 * 12px slot inside a 16px circle, visibly breaking the circle.
 */
export const buttonIconRecipe = cva([
  'inline-flex shrink-0 items-center justify-center',
  'size-[var(--ds-btn-icon-size)] text-[length:var(--ds-btn-icon-size)]',
  '[&_svg]:size-full',
]);
